import { PagedSchoolsResponse, School } from "../interfaces/school";
import { fetchWithAuth } from "./fetchWithAuth";

const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";
const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;

export const getSchoolsWithParams = async (
  pageNumber = 1,
  pageSize = 10,
  searchTerm = "",
): Promise<PagedSchoolsResponse> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const params = new URLSearchParams({
    pageNumber: pageNumber.toString(),
    pageSize: pageSize.toString(),
    searchTerm,
  });

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/School/GetSchoolsWithParams?${params.toString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch schools");
  }

  const data: PagedSchoolsResponse = await response.json();
  if (!data || !Array.isArray(data.schools)) {
    throw new Error("Invalid response structure");
  }

  return data;
};

export const getSchool = async (schoolID: number): Promise<School> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/School/GetSchoolById/${schoolID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch school");
  }

  return response.json();
};

export const createSchool = async (school: School): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/School/CreateSchool`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(school),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to create school");
  }
};

export const updateSchool = async (school: School): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/School/UpdateSchool/${school.schoolID}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(school),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to update school");
  }
};

export const deleteSchool = async (schoolID: number): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/School/DeleteSchool/${schoolID}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to delete school");
  }
};
